import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Chip, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import { fetchClientInvoicingReport } from "../../../../../services/reports";
import { buildClientInvoicingSalesOrderUrl } from "../../../reports/client-invoicing/salesOrderUrl";
import FinanceSectionShell from "./FinanceSectionShell";
import FinanceAssigneeAvatars, { getFinanceOrderAssignees } from "./FinanceAssigneeAvatars";
import { getReadyToInvoiceProgress } from "./financeInvoiceProgress";

const money = (value, currency = "CAD") => Number(value || 0).toLocaleString(undefined, { style: "currency", currency, minimumFractionDigits: 2 });

export const getReadyToInvoiceAssignedReps = getFinanceOrderAssignees;

export default function FinanceReadyToInvoice({ instanceUniqueId, limit = 8 }) {
  const [state, setState] = useState({ loading: true, error: "", rows: [], summary: {} });
  const load = useCallback(async () => {
    setState((current) => ({ ...current, loading: true, error: "" }));
    try {
      const response = await fetchClientInvoicingReport({ instanceUniqueId, soStatus: "ready_to_invoice" });
      setState({ loading: false, error: "", rows: Array.isArray(response?.rows) ? response.rows : [], summary: response?.summary || {} });
    } catch (_) {
      setState({ loading: false, error: "We couldn't load invoice-ready projects.", rows: [], summary: {} });
    }
  }, [instanceUniqueId]);
  useEffect(() => { load(); }, [load]);

  return (
    <FinanceSectionShell
      title="Ready to Invoice" subtitle="Sales orders that are ready for client invoicing."
      countLabel={`${state.summary?.totalReadyForInvoicing ?? state.rows.length} ready`}
      loading={state.loading} error={state.error} onRetry={load} empty={!state.rows.length}
      emptyTitle="You're caught up" emptyBody="No sales orders are currently ready to invoice."
      viewAllHref={`/instance/${encodeURIComponent(instanceUniqueId)}/reports/client-invoicing`}
      viewAllLabel="View all ready to invoice"
    >
      <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
        {state.rows.slice(0, limit).map((row, index) => {
          const url = buildClientInvoicingSalesOrderUrl(instanceUniqueId, row);
          const financialProgress = getReadyToInvoiceProgress(row);
          return (
            <Box key={row.salesOrderId || `${row.orderId}-${index}`} sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr auto", md: "minmax(180px,1.4fr) minmax(130px,0.8fr) 120px 150px 80px auto" }, gap: 1.5, alignItems: "center" }}>
              <Box><Typography fontWeight={750}>{row.clientName || "—"}</Typography><Typography variant="caption" color="text.secondary">{row.projectTitle || `Order ${row.orderId || "—"}`}</Typography></Box>
              <Typography variant="body2" sx={{ display: { xs: "none", md: "block" } }}>SO #{row.salesOrderNumber || row.salesOrderId || "—"}</Typography>
              <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={700} aria-label="Remaining to invoice">{money(row.uninvoicedAmount ?? row.estimatedInvoiceAmount, row.currency || "CAD")}</Typography>
                {financialProgress.hasInvoice && (
                  <Tooltip
                    arrow
                    title={(
                      <Stack spacing={0.25}>
                        {financialProgress.expected > 0 && <span>Expected invoice: {money(financialProgress.expected, row.currency || "CAD")}</span>}
                        <span>Already invoiced: {money(financialProgress.invoiced, row.currency || "CAD")}</span>
                        {financialProgress.paid > 0 && <span>Paid: {money(financialProgress.paid, row.currency || "CAD")}</span>}
                        <span>Remaining: {money(financialProgress.remaining, row.currency || "CAD")}</span>
                      </Stack>
                    )}
                  >
                    <Box sx={{ mt: 0.35, maxWidth: 150 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <LinearProgress variant="determinate" value={financialProgress.progress} sx={{ flex: 1, height: 4, borderRadius: 999 }} />
                        <Typography variant="caption" color="text.secondary">{Math.round(financialProgress.progress)}%</Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.4} alignItems="center" sx={{ mt: 0.2, color: "text.secondary" }}>
                        {financialProgress.hasPayment ? <PaymentOutlinedIcon sx={{ fontSize: 13 }} /> : <ReceiptOutlinedIcon sx={{ fontSize: 13 }} />}
                        <Typography variant="caption" noWrap>
                          {money(financialProgress.hasPayment ? financialProgress.paid : financialProgress.invoiced, row.currency || "CAD")} of {money(financialProgress.expected, row.currency || "CAD")} {financialProgress.hasPayment ? "paid" : "invoiced"}
                        </Typography>
                      </Stack>
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <Chip size="small" variant="outlined" label={row.salesOrderStatus || "Ready to Invoice"} sx={{ display: { xs: "none", md: "inline-flex" }, justifySelf: "start", color: "var(--ec-status-accent-text, #5b21b6)", backgroundColor: "var(--ec-status-accent-bg, #f5f3ff)", borderColor: "var(--ec-status-accent-border, #c4b5fd)", fontWeight: 700 }} />
              <FinanceAssigneeAvatars row={row} />
              <Button component="a" href={url || undefined} target="_blank" rel="noopener noreferrer" disabled={!url} size="small" variant="outlined" sx={{ textTransform: "none" }}>Open</Button>
            </Box>
          );
        })}
      </Stack>
    </FinanceSectionShell>
  );
}
